'use client';

import { FC, useState, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import Image from 'next/image';

// Share icon component
const ShareIcon = ({
  className,
  size = 24,
}: {
  className?: string;
  size?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M17 22C16.1667 22 15.4583 21.7083 14.875 21.125C14.2917 20.5417 14 19.8333 14 19C14 18.9 14.025 18.6667 14.075 18.3L7.05 14.2C6.78333 14.45 6.475 14.646 6.125 14.788C5.775 14.93 5.4 15.0007 5 15C4.16667 15 3.45833 14.7083 2.875 14.125C2.29167 13.5417 2 12.8333 2 12C2 11.1667 2.29167 10.4583 2.875 9.875C3.45833 9.29167 4.16667 9 5 9C5.4 9 5.775 9.071 6.125 9.213C6.475 9.355 6.78333 9.55067 7.05 9.8L14.075 5.7C14.0417 5.58333 14.021 5.471 14.013 5.363C14.005 5.255 14.0007 5.134 14 5C14 4.16667 14.2917 3.45833 14.875 2.875C15.4583 2.29167 16.1667 2 17 2C17.8333 2 18.5417 2.29167 19.125 2.875C19.7083 3.45833 20 4.16667 20 5C20 5.83333 19.7083 6.54167 19.125 7.125C18.5417 7.70833 17.8333 8 17 8C16.6 8 16.225 7.929 15.875 7.787C15.525 7.645 15.2167 7.44933 14.95 7.2L7.925 11.3C7.95833 11.4167 7.97933 11.5293 7.988 11.638C7.99667 11.7467 8.00067 11.8673 8 12C7.99933 12.1327 7.99533 12.2537 7.988 12.363C7.98067 12.4723 7.95967 12.5847 7.925 12.7L14.95 16.8C15.2167 16.55 15.525 16.3543 15.875 16.213C16.225 16.0717 16.6 16.0007 17 16C17.8333 16 18.5417 16.2917 19.125 16.875C19.7083 17.4583 20 18.1667 20 19C20 19.8333 19.7083 20.5417 19.125 21.125C18.5417 21.7083 17.8333 22 17 22Z"
      fill="#05F273"
    />
  </svg>
);

// Play button icon
const PlayButton = ({ small = false }: { small?: boolean }) => (
  <div
    className={`${small ? 'w-[46px] h-[46px]' : 'w-[80px] h-[80px] md:w-[100px] md:h-[100px]'} flex items-center justify-center cursor-pointer transition-transform hover:scale-110`}
  >
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="40" cy="40" r="40" fill="white" fillOpacity="0.2" />
      <circle cx="40" cy="40" r="32" fill="white" />
      <path d="M51 40L34.5 50.3923L34.5 29.6077L51 40Z" fill="#0E0F0F" />
    </svg>
  </div>
);

// Type guards for Prismic embed fields
const hasEmbedHtml = (v: unknown): v is { html: string } => {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Record<string, unknown>).html === 'string'
  );
};

const hasEmbedUrl = (
  v: unknown,
): v is { embed_url: string; thumbnail_url?: string } => {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Record<string, unknown>).embed_url === 'string'
  );
};

// Video Player Component
const VideoPlayer = ({
  video,
  videoiframe,
  thumbnailUrl,
  isPlaying,
  onPlay,
  small = false,
}: {
  video: unknown;
  videoiframe?: string | null;
  thumbnailUrl: string | null | undefined;
  isPlaying: boolean;
  onPlay: () => void;
  small?: boolean;
}) => {
  // Show thumbnail with play button if not playing and we have a video embed
  if (!isPlaying && video && hasEmbedUrl(video)) {
    return (
      <div
        className="relative w-full h-full cursor-pointer group"
        onClick={onPlay}
      >
        {thumbnailUrl && (
          <Image
            src={thumbnailUrl}
            alt="Video thumbnail"
            fill
            className="object-cover"
            unoptimized
          />
        )}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <PlayButton small={small} />
        </div>
      </div>
    );
  }

  // Play the embed video if available
  if (video && hasEmbedHtml(video)) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: video.html.replace(
            /<iframe/,
            '<iframe style="width:100%;height:100%"',
          ),
        }}
        className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
      />
    );
  }

  // Fallback to videoiframe field if video embed is not available
  if (videoiframe) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: videoiframe.replace(
            /<iframe/,
            '<iframe style="width:100%;height:100%"',
          ),
        }}
        className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
      />
    );
  }

  return (
    <div className="w-full h-full bg-[#0e1110] flex items-center justify-center">
      <p className="text-white/50">No video available</p>
    </div>
  );
};

/**
 * Props for `FeaturedVideo`.
 */
export type FeaturedVideoProps =
  SliceComponentProps<Content.FeaturedVideoSlice>;

/**
 * Component for "FeaturedVideo" Slices.
 */
const FeaturedVideo: FC<FeaturedVideoProps> = ({ slice }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const { title, video, share_link, videoiframe } = slice.primary;
  const description =
    'description' in slice.primary ? slice.primary.description : null;
  const isWithDescription = slice.variation === 'withDescription';

  // Get video thumbnail URL if available
  const thumbnailUrl = hasEmbedUrl(video) ? video.thumbnail_url : null;

  // Animation refs
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const cardElement = cardRef.current;
    if (cardElement) {
      observer.observe(cardElement);
    }

    return () => {
      if (cardElement) {
        observer.unobserve(cardElement);
      }
    };
  }, []);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  // Variation: withDescription - Horizontal layout
  if (isWithDescription) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="w-full px-4 md:px-8 lg:px-[200px] py-[60px]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 15.895%, rgba(14, 17, 16, 0.5) 100%), linear-gradient(rgb(14, 17, 16) 63.331%, rgb(15, 66, 58) 100%)',
        }}
      >
        <div className="max-w-[1040px] mx-auto">
          {/* Horizontal Card Container */}
          <div
            ref={cardRef}
            className="flex flex-col md:flex-row gap-6 items-center justify-center p-6 rounded-[16px] shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] opacity-0 translate-y-8 transition-all duration-700 ease-out"
            style={{
              background:
                'linear-gradient(to bottom, #002726 0%, rgba(0, 0, 0, 0.2) 100%)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          >
            {/* Video Container - Left Side */}
            <div className="relative w-full md:w-[709px] h-[250px] md:h-[398px] shrink-0 overflow-hidden">
              <VideoPlayer
                video={video}
                videoiframe={videoiframe}
                thumbnailUrl={thumbnailUrl}
                isPlaying={isPlaying}
                onPlay={handlePlay}
                small
              />
            </div>

            {/* Content - Right Side */}
            <div className="flex flex-col gap-6 items-start flex-1 min-w-0">
              {/* Title */}
              {isFilled.richText(title) && (
                <div className="font-medium text-[20px] md:text-[24px] leading-[1.2] text-white capitalize">
                  <PrismicRichText field={title} />
                </div>
              )}

              {/* Description */}
              {description && isFilled.richText(description) && (
                <div className="font-light text-[14px] md:text-[16px] leading-[1.4] text-white">
                  <PrismicRichText field={description} />
                </div>
              )}

              {/* Share Link */}
              {isFilled.link(share_link) && (
                <PrismicNextLink
                  field={share_link}
                  className="flex items-center gap-2 text-[#05f273] hover:opacity-80 transition-opacity"
                >
                  <ShareIcon size={24} />
                  <span className="font-medium text-[16px] leading-[1.2] capitalize">
                    Share
                  </span>
                </PrismicNextLink>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default variation - Vertical layout
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0e1110] w-full px-4 md:px-8 lg:px-[200px] py-[60px]"
    >
      <div className="max-w-[1040px] mx-auto">
        {/* Video Card Container */}
        <div
          ref={cardRef}
          className="relative flex flex-col gap-6 items-center justify-center p-6 rounded-[24px] bg-[#002726] opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Header: Title and Share */}
          <div className="flex items-center justify-between w-full">
            {/* Title */}
            {isFilled.richText(title) && (
              <div className="font-medium text-[20px] md:text-[24px] leading-[1.2] text-white capitalize">
                <PrismicRichText field={title} />
              </div>
            )}

            {/* Share Link */}
            {isFilled.link(share_link) && (
              <PrismicNextLink
                field={share_link}
                className="flex items-center gap-2 text-[#05f273] hover:opacity-80 transition-opacity"
              >
                <ShareIcon size={24} />
                <span className="font-medium text-[16px] leading-[1.2] capitalize">
                  Share
                </span>
              </PrismicNextLink>
            )}
          </div>

          {/* Video Container */}
          <div className="relative w-full h-[300px] md:h-[500px] rounded-lg overflow-hidden">
            <VideoPlayer
              video={video}
              videoiframe={videoiframe}
              thumbnailUrl={thumbnailUrl}
              isPlaying={isPlaying}
              onPlay={handlePlay}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedVideo;
